import React, {useMemo, useState} from 'react';
import {DirectionalHint, Icon, Spinner, SpinnerSize, Text, TooltipHost, useTheme} from '@fluentui/react';
import {useGetSavedSearchQuery} from '../../store/endpoints/SavedSearchEndpoints';
import {criteriaToSugarTokens, sugarChipLabel} from '../GlobalSearch/productQueryParser';
import {ISearchRequest} from '../../models/Search';

interface ISavedSearchPeekProps {
    savedSearchId?: string;
    name?: string;
    children: React.ReactElement;
    directionalHint?: DirectionalHint;
}

/** Human lines for a saved search's criteria: the omnibox chip labels for what sugar can express, plus the rest. */
export const describeCriteria = (criteria?: Partial<ISearchRequest>): string[] => {
    if (!criteria) return [];
    const lines: string[] = [];
    const {chips, keywords} = criteriaToSugarTokens(criteria);
    if (keywords.trim()) lines.push(`Keywords: ${keywords.trim()}`);
    for (const chip of chips) lines.push(sugarChipLabel(chip));
    for (const ex of criteria.exclude ?? []) lines.push(`${ex.name} ≠ ${ex.value} (excluded)`);
    if (criteria.inStock) lines.push('In stock only');
    if (criteria.soldBefore) lines.push('Sold before');
    if (criteria.inStorefront) lines.push('Listed on a storefront');
    if (criteria.storefrontFilter?.length) lines.push(`${criteria.storefrontFilter.length} storefront${criteria.storefrontFilter.length === 1 ? '' : 's'}`);
    if (criteria.tags?.length) lines.push(`Tags: ${criteria.tags.join(', ')}`);
    if (criteria.userTags?.length) lines.push(`My tags: ${criteria.userTags.map((t) => t.split('||').pop()).join(', ')}`);
    if (criteria.productLines?.length) lines.push(`Product lines: ${criteria.productLines.join(', ')}`);
    if (criteria.inStockVendors?.length) lines.push(`In stock at: ${criteria.inStockVendors.join(', ')}`);
    if (criteria.inCatalogVendors?.length) lines.push(`Carried by: ${criteria.inCatalogVendors.join(', ')}`);
    if (criteria.sortField) lines.push(`Sorted by ${criteria.sortField}${criteria.sortDirection ? ` ${criteria.sortDirection}` : ''}`);
    return lines;
};

/**
 * Hover flyout on a saved search card that says what the search actually looks for. The full saved
 * search (the refs strip only carries names) loads on first hover.
 */
const SavedSearchPeek: React.FC<ISavedSearchPeekProps> = ({savedSearchId, name, children, directionalHint}) => {
    const theme = useTheme();
    const [peeked, setPeeked] = useState(false);
    const saved = useGetSavedSearchQuery({id: savedSearchId ?? ''}, {skip: !peeked || !savedSearchId});
    const lines = useMemo(() => describeCriteria(saved.data?.criteria), [saved.data]);

    const content = (
        <div style={{maxWidth: 360, minWidth: 200}}>
            <Text variant='mediumPlus' block styles={{root: {fontWeight: 600, marginBottom: 4}}}>
                <Icon iconName='SearchBookmark' styles={{root: {marginRight: 6, color: theme.palette.themePrimary, verticalAlign: 'text-bottom'}}} aria-hidden />
                {name ?? saved.data?.name ?? 'Saved search'}
            </Text>
            {saved.isFetching && <Spinner size={SpinnerSize.xSmall} label='Loading criteria…' labelPosition='right' />}
            {!saved.isFetching && saved.data && lines.length === 0 && (
                <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary}}}>No criteria: matches the whole catalog.</Text>
            )}
            {lines.map((line, i) => (
                <Text key={i} variant='small' block styles={{root: {lineHeight: '18px'}}}>• {line}</Text>
            ))}
            {!saved.isFetching && saved.data && (
                <Text variant='small' block styles={{root: {marginTop: 6, color: theme.palette.neutralSecondary}}}>Click the card to run this search</Text>
            )}
        </div>
    );

    return (
        <TooltipHost
            content={content}
            directionalHint={directionalHint ?? DirectionalHint.topCenter}
            calloutProps={{gapSpace: 4}}
            onTooltipToggle={(visible) => { if (visible) setPeeked(true); }}
        >
            {children}
        </TooltipHost>
    );
};

export default SavedSearchPeek;
